import dayjs, { type OpUnitType } from "dayjs";

import type {
    AdIntelligenceEnumsPlacementType,
    AdIntelligenceEnumsScreenshotFilter,
    AdIntelligenceEnumsVerificationDevice,
    AdIntelligenceRecordsVerificationOverviewRecord,
} from "@similarweb/pro-api-client";

import type { IVerificationFilterState } from "./verification-filters.types";

export type VerificationRecord = AdIntelligenceRecordsVerificationOverviewRecord;
export type PublisherEntry = [string, VerificationRecord[]];

export const PUBLISHERS_PER_PAGE = 10;

const VERIFICATION_EXCEL_ENDPOINT = "/api/AdIntelligence/Advertiser/publisher-verifications/excel";

export interface IVerificationExcelParams {
    advertiser: string;
    country: number;
    dateFrom: string;
    dateTo: string;
    device?: AdIntelligenceEnumsVerificationDevice;
    screenshotFilter?: AdIntelligenceEnumsScreenshotFilter;
    position?: AdIntelligenceEnumsPlacementType[];
    campaignName?: string[];
    publishers?: string[];
}

// Array params (position/campaignName/publishers) are serialized as repeated keys to match the
// endpoint's OpenAPI contract (style=form, explode=true), e.g. position=A&position=B — not CSV.
export const buildVerificationExcelUrl = (params: IVerificationExcelParams): string => {
    const entries: string[][] = [];
    Object.entries(params).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }
        if (Array.isArray(value)) {
            value.forEach((item) => entries.push([key, String(item)]));
        } else {
            entries.push([key, String(value)]);
        }
    });
    return `${VERIFICATION_EXCEL_ENDPOINT}?${new URLSearchParams(entries).toString()}`;
};

const PUBLISHER_LABEL_REGEX = /^(.+)\s+\((\d+)\)$/;

export const sumRecordCounts = (records: VerificationRecord[]): number => records.reduce((sum, r) => sum + r.count, 0);

export const formatRowKey = (
    publisher: string,
    records: VerificationRecord[],
    valueOf: (records: VerificationRecord[]) => number = sumRecordCounts,
): string => `${publisher} (${valueOf(records)})`;

export interface IParsedPublisherLabel {
    name: string;
    count: string | null;
}

export const parsePublisherLabel = (key: string): IParsedPublisherLabel => {
    const match = key.match(PUBLISHER_LABEL_REGEX);

    if (!match) {
        return { name: key, count: null };
    }

    return { name: match[1], count: match[2] };
};

export const sortPublishersByCount = (
    rawData: Record<string, VerificationRecord[] | null>,
    valueOf: (records: VerificationRecord[]) => number = sumRecordCounts,
): PublisherEntry[] => {
    const withSums: Array<{ entry: PublisherEntry; sum: number }> = [];

    for (const [publisher, records] of Object.entries(rawData)) {
        if (records === null) {
            continue;
        }
        withSums.push({ entry: [publisher, records], sum: valueOf(records) });
    }

    withSums.sort((a, b) => b.sum - a.sum);
    return withSums.map(({ entry }) => entry);
};

export const getPageSliceAsEventsByRow = (
    sorted: PublisherEntry[],
    page: number,
    perPage: number,
    valueOf: (records: VerificationRecord[]) => number = sumRecordCounts,
): Record<string, VerificationRecord[]> => {
    const start = (page - 1) * perPage;
    const pageSlice = sorted.slice(start, start + perPage);
    const eventsByRow: Record<string, VerificationRecord[]> = {};

    for (const [publisher, records] of pageSlice) {
        eventsByRow[formatRowKey(publisher, records, valueOf)] = records;
    }

    return eventsByRow;
};

export const getMaxCellValue = (data: Record<string, Record<string, number>>): number => {
    let max = 0;

    for (const row of Object.values(data)) {
        for (const value of Object.values(row)) {
            if (value > max) {
                max = value;
            }
        }
    }

    return max;
};

export const getTotalPages = (count: number, perPage: number): number => Math.max(Math.ceil(count / perPage), 1);

export const clampPage = (page: number, totalPages: number): number => Math.min(Math.max(page, 1), totalPages);

export const resolveActiveTab = (tabs: string[], activeTab: string, fallback = ""): string => {
    if (tabs.includes(activeTab)) {
        return activeTab;
    }

    return tabs[0] ?? fallback;
};

export interface IPublisherOption {
    id: string;
    value: string;
    label: string;
}

export const isNaValue = (s: string | null | undefined): boolean => typeof s === "string" && s.toLowerCase() === "na";

export const buildFilterPublisherOptions = (publishers: readonly string[] | null | undefined): IPublisherOption[] =>
    (publishers ?? [])
        .filter((publisher) => Boolean(publisher) && !isNaValue(publisher))
        .map((publisher) => ({
            id: publisher,
            value: publisher,
            label: publisher,
        }));

export const buildCampaignOptions = (campaigns?: string[] | null): IPublisherOption[] => {
    const seen = new Set<string>();
    const options: IPublisherOption[] = [];

    for (const campaign of campaigns ?? []) {
        const value = campaign;
        if (!value || isNaValue(value) || seen.has(value)) {
            continue;
        }
        seen.add(value);
        const title = campaign;
        const label = title && !isNaValue(title) ? title : value;
        options.push({ id: value, value, label });
    }

    return options;
};

export const getScreenshotsCount = (events: VerificationRecord[]): number =>
    events.reduce((sum, r) => sum + r.screenshotCount, 0);

export const formatPositionLabel = (position: string, unknownLabel: string): string =>
    position.toLowerCase() === "na" ? unknownLabel : position;

// The unit range (start/end of day, ISO week, or month) the cell represents, clamped to the
// selected date range. In weekly/monthly views the first and last cells can spill past the picked
// range to fill out a whole unit, but the cell only aggregates in-range data. Clamping keeps both
// the tooltip label and the details drawer query aligned with what the cell actually shows.
export const getBucketUnitRange = (
    bucket: { dateFrom: string; unitRangeConfig: OpUnitType },
    range?: { from?: string; to?: string },
) => {
    // Weeks are grouped by ISO week (Monday-based) when building the heatmap, so the unit range must
    // use the same boundary — otherwise the queried range is offset by a day from the cell's bucket.
    const boundaryUnit = bucket.unitRangeConfig === "week" ? "isoWeek" : bucket.unitRangeConfig;

    // Parse and compute in UTC: date-only strings would otherwise be read in the local timezone
    // and shifted by toISOString(), landing week/month boundaries on the wrong day for non-UTC users.
    let from = dayjs.utc(bucket.dateFrom).startOf(boundaryUnit);
    let to = dayjs.utc(bucket.dateFrom).endOf(boundaryUnit);

    const rangeFrom = range?.from ? dayjs.utc(range.from) : undefined;
    const rangeTo = range?.to ? dayjs.utc(range.to) : undefined;

    if (rangeFrom?.isValid() && rangeFrom.isAfter(from)) {
        from = rangeFrom;
    }
    if (rangeTo?.isValid() && rangeTo.isBefore(to)) {
        // endOf("day") so the clamped upper bound stays end-of-day (23:59:59.999Z), matching the
        // unclamped endOf(unit); a bare start-of-day bound would drop the boundary day's events.
        to = rangeTo.endOf("day");
    }

    return {
        dateFrom: from.toISOString(),
        dateTo: to.toISOString(),
    };
};

export const formatDateRange = (dateFrom: string, dateTo: string): string => {
    // Format in UTC to stay aligned with the UTC ranges from getBucketUnitRange; parsing the
    // 23:59:59.999Z end bound locally would roll the label to the next day in UTC-ahead timezones.
    const from = dayjs.utc(dateFrom);
    const to = dayjs.utc(dateTo);

    if (!from.isValid() || !to.isValid()) {
        return `${dateFrom} – ${dateTo}`;
    }

    if (from.isSame(to, "day")) {
        return from.format("MMM D, YYYY");
    }

    if (from.isSame(to, "year")) {
        return `${from.format("MMM D")} – ${to.format("MMM D, YYYY")}`;
    }

    return `${from.format("MMM D, YYYY")} – ${to.format("MMM D, YYYY")}`;
};

const FILTER_TRACK_NAMES: Record<keyof IVerificationFilterState, string> = {
    publishers: "Publishers",
    campaigns: "Campaigns",
    adPositions: "Ad position",
};

const isFilterValueChanged = (prev?: string[], next?: string[]): boolean => {
    const prevList = prev ?? [];
    const nextList = next ?? [];
    if (prevList.length !== nextList.length) {
        return true;
    }
    const prevSet = new Set(prevList);
    return nextList.some((v) => !prevSet.has(v));
};

export const getChangedFilterName = (prev: IVerificationFilterState, next: IVerificationFilterState): string | null => {
    const keys = Object.keys(FILTER_TRACK_NAMES) as (keyof IVerificationFilterState)[];
    for (const key of keys) {
        if (isFilterValueChanged(prev[key], next[key])) {
            return FILTER_TRACK_NAMES[key];
        }
    }
    return null;
};

export function ensureAbsoluteUrl(url?: string) {
    if (!url) {
        return "";
    }
    const urlClean = url.trim();
    // If it doesn't start with http:// or https://, prepend https://
    return /^https?:\/\//i.test(urlClean) ? urlClean : `https://${urlClean}`;
}

export function openInNewTab(url: string) {
    window.open(ensureAbsoluteUrl(url), "_blank");
}
